import { MapPin, Star, BadgeCheck, Sparkles, Users, ArrowUpRight } from "lucide-react";
import { Badge } from "./ui/badge";
import { getImageUrl, formatPriceRange } from "../lib/api";

function PlanBadge({ center }) {
    if (center.plan_type === "elite" || center.plan_level === 3) {
        return (
            <Badge
                data-testid="badge-elite"
                className="border-0 bg-gradient-to-r from-amber-500 to-amber-300 text-slate-950"
            >
                <Sparkles className="mr-1 h-3 w-3" /> Elite
            </Badge>
        );
    }
    if (center.is_premium || center.plan_level >= 2) {
        return (
            <Badge
                data-testid="badge-premium"
                className="border-0 bg-cyan-500 text-slate-950"
            >
                Premium
            </Badge>
        );
    }
    if (center.is_verified) {
        return (
            <Badge
                data-testid="badge-verified"
                variant="outline"
                className="border-cyan-500/40 bg-cyan-500/10 text-cyan-300"
            >
                <BadgeCheck className="mr-1 h-3 w-3" /> Verificado
            </Badge>
        );
    }
    return null;
}

export default function CenterCard({ center }) {
    const image =
        getImageUrl(center.logo) ||
        getImageUrl((center.gallery && center.gallery[0]) || null) ||
        "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=800&q=80";

    const modalities = Array.isArray(center.modality) ? center.modality : [];
    const priceLabel = formatPriceRange(center.price_min, center.price_max);
    const profileUrl = `https://sinadicciones.org/centros/${center.slug || center.center_id}`;

    return (
        <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`center-card-${center.center_id}`}
            className="card-hover group relative flex h-full flex-col overflow-hidden rounded-xl border border-slate-800 bg-slate-900"
        >
            <div className="relative aspect-[16/10] overflow-hidden bg-slate-800">
                <img
                    src={image}
                    alt={`${center.name} - centro de rehabilitación en ${center.city || "Chile"}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute left-3 top-3 flex gap-2">
                    <PlanBadge center={center} />
                </div>
                {center.average_rating > 0 ? (
                    <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-slate-950/80 px-2 py-1 text-xs font-semibold text-amber-300 backdrop-blur">
                        <Star className="h-3 w-3 fill-amber-300" />
                        {center.average_rating.toFixed(1)}
                    </div>
                ) : null}
            </div>

            <div className="flex flex-1 flex-col gap-3 p-5">
                <div>
                    <h3 className="line-clamp-2 text-base font-semibold text-slate-50 group-hover:text-cyan-400">
                        {center.name}
                    </h3>
                    <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">
                            {center.city || "—"}
                            {center.region ? `, ${center.region}` : ""}
                        </span>
                    </p>
                </div>

                {modalities.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                        {modalities.slice(0, 3).map((m) => (
                            <span
                                key={m}
                                className="rounded-md border border-slate-700 bg-slate-950 px-2 py-0.5 text-[11px] text-slate-300"
                            >
                                {m}
                            </span>
                        ))}
                    </div>
                ) : null}

                {center.description ? (
                    <p className="line-clamp-2 text-sm text-slate-400">
                        {center.description}
                    </p>
                ) : null}

                <div className="mt-auto flex items-end justify-between pt-2">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-500">
                            Desde
                        </div>
                        <div className="text-sm font-semibold text-cyan-400">
                            {priceLabel}
                        </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-300 group-hover:text-cyan-400">
                        Ver perfil <ArrowUpRight className="h-3.5 w-3.5" />
                    </div>
                </div>

                {center.capacity ? (
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                        <Users className="h-3 w-3" /> Capacidad {center.capacity}
                    </div>
                ) : null}
            </div>
        </a>
    );
}
